import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../context/AppContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { ArrowLeft, Copy, Share2, MessageCircle, Facebook, Twitter, Send, Download, Home, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { publicAPI } from "../lib/api";

const DEFAULT_TEMPLATE = ({ appName, tagline, url }) => `🏘️ *${appName || "SAPA WARGA DM RESIDENCE"}*
${tagline || "GUYUB - RUKUN - SAUYUNAN"}

Assalamu'alaikum Wr. Wb.
Kepada Yth. Bapak/Ibu Warga DM Residence 🙏

Mari bergabung di aplikasi digital resmi warga kita — semua informasi lingkungan, iuran K3/IPL, laporan kas RT, pengumuman, kegiatan, dan kontak darurat kini bisa diakses dalam satu genggaman.

✨ Fitur unggulan:
• Info Iuran & Kas RT (transparan realtime)
• Pengumuman & Kegiatan warga
• Laporan Kejadian & Kontak Darurat
• Jadwal Satpam & Layanan Lingkungan
• Data warga & UMKM

👉 Buka aplikasi di sini:
${url}

Silakan simpan dan sebarkan link ini ke tetangga blok lain 🙏
Terima kasih atas partisipasinya.

Wassalamu'alaikum Wr. Wb.`;

const BagikanUndangan = () => {
  const { theme } = useApp();
  const nav = useNavigate();
  const [summary, setSummary] = useState(null);
  const [copied, setCopied] = useState(false);
  const [message, setMessage] = useState("");
  const [edited, setEdited] = useState(false);

  useEffect(() => {
    publicAPI.summary().then(setSummary).catch(() => {});
  }, []);

  const shareUrl = typeof window !== "undefined" ? window.location.origin + "/login" : "";
  const s = summary?.settings || {};
  const appName = s.appName || theme.appName || "WARGA RT DM RESIDENCE";
  const tagline = s.appTagline || theme.appTagline || "GUYUB - RUKUN - SAUYUNAN";
  const liveLogo = s.logo || theme.logo;

  const defaultMsg = useMemo(() => DEFAULT_TEMPLATE({ appName, tagline, url: shareUrl }), [appName, tagline, shareUrl]);
  useEffect(() => { if (!edited) setMessage(defaultMsg); }, [defaultMsg, edited]);

  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=500x500&margin=15&data=${encodeURIComponent(shareUrl)}`;

  const doCopy = async (text, label = "Pesan") => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      toast.success(`${label} berhasil disalin!`);
      setTimeout(() => setCopied(false), 2500);
    } catch { toast.error("Gagal menyalin. Silakan copy manual."); }
  };

  const shareWA = () => {
    window.open(`https://wa.me/?text=${encodeURIComponent(message)}`, "_blank");
  };
  const shareTelegram = () => {
    window.open(`https://t.me/share/url?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(message)}`, "_blank");
  };
  const shareFB = () => {
    window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, "_blank");
  };
  const shareTwitter = () => {
    window.open(`https://twitter.com/intent/tweet?text=${encodeURIComponent(message)}`, "_blank");
  };
  const nativeShare = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: appName, text: message, url: shareUrl }); }
      catch (e) { if (e?.name !== "AbortError") toast.error("Gagal membagikan"); }
    } else {
      doCopy(message, "Pesan");
    }
  };

  const downloadQR = () => {
    const a = document.createElement("a");
    a.href = qrUrl;
    a.download = `undangan-${(appName || "warga").replace(/\s+/g, "-").toLowerCase()}.png`;
    a.target = "_blank";
    a.rel = "noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50">
      {/* Top bar */}
      <div className="sticky top-0 z-30 bg-white/90 backdrop-blur border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button onClick={() => nav(-1)} className="h-9 w-9 grid place-items-center rounded-full hover:bg-slate-100" data-testid="undangan-back-btn"><ArrowLeft className="h-5 w-5" /></button>
            {liveLogo && <img src={liveLogo} alt="logo" className="h-9 w-9 rounded-full object-cover ring-1 ring-emerald-200" />}
            <div className="leading-tight">
              <div className="font-extrabold text-slate-800 text-sm" style={{ fontFamily: "Poppins, sans-serif" }}>{appName}</div>
              <div className="text-[10px] text-emerald-700 font-bold tracking-widest">BAGIKAN UNDANGAN</div>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={() => nav("/login")} className="text-slate-600" data-testid="undangan-home-btn"><Home className="h-4 w-4 mr-1" />Beranda</Button>
        </div>
      </div>

      {/* Hero */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 pt-10 pb-6">
        <div className="text-center max-w-2xl mx-auto">
          <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 mb-4"><Sparkles className="h-3 w-3 mr-1" />Ajak Tetangga Bergabung</Badge>
          <h1 className="text-3xl md:text-5xl font-extrabold text-slate-900 leading-tight" style={{ fontFamily: "Poppins, sans-serif" }}>
            Bagikan Undangan <span className="text-emerald-600">Warga DM Residence</span>
          </h1>
          <p className="mt-3 text-slate-600 text-sm md:text-base leading-relaxed">
            Undang tetangga blok lain untuk bergabung di aplikasi digital warga. Cukup satu tap — kirim lewat WhatsApp, copy link, atau bagikan QR code di bawah ini.
          </p>
        </div>
      </div>

      {/* Main grid */}
      <div className="max-w-6xl mx-auto px-4 lg:px-8 pb-16">
        <div className="grid lg:grid-cols-5 gap-6">
          {/* Left: Message template + share buttons */}
          <div className="lg:col-span-3 space-y-4">
            <Card className="border-emerald-200 shadow-lg">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                  <div>
                    <h3 className="font-bold text-slate-800 text-lg" style={{ fontFamily: "Poppins, sans-serif" }}>Pesan Undangan</h3>
                    <p className="text-xs text-slate-500">Silakan edit sesuai kebutuhan sebelum dibagikan</p>
                  </div>
                  {edited && <Badge variant="secondary" className="bg-amber-100 text-amber-800">Diedit</Badge>}
                </div>
                <Textarea
                  value={message}
                  onChange={(e) => { setMessage(e.target.value); setEdited(true); }}
                  rows={14}
                  className="font-mono text-xs md:text-sm bg-slate-50 border-slate-200 focus:border-emerald-400 resize-none"
                  data-testid="undangan-message-textarea"
                />
                <div className="flex items-center justify-between mt-3 gap-2 flex-wrap">
                  <div className="text-xs text-slate-500">{message.length} karakter</div>
                  <div className="flex gap-2">
                    {edited && <Button size="sm" variant="outline" onClick={() => { setEdited(false); setMessage(defaultMsg); }} data-testid="undangan-reset-btn">Reset</Button>}
                    <Button size="sm" onClick={() => doCopy(message, "Pesan")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="undangan-copy-message-btn">
                      {copied ? <><CheckCircle2 className="h-4 w-4 mr-1" />Tersalin</> : <><Copy className="h-4 w-4 mr-1" />Salin Pesan</>}
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Share buttons */}
            <Card className="border-slate-200">
              <CardContent className="p-6">
                <h3 className="font-bold text-slate-800 text-lg mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>Bagikan Sekarang</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  <button onClick={shareWA} className="group flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-emerald-200 bg-emerald-50 hover:bg-emerald-100 hover:border-emerald-400 transition" data-testid="undangan-share-wa-btn">
                    <div className="h-12 w-12 rounded-full bg-emerald-500 text-white grid place-items-center group-hover:scale-110 transition"><MessageCircle className="h-6 w-6" /></div>
                    <div className="font-bold text-sm text-emerald-800">WhatsApp</div>
                    <div className="text-[10px] text-emerald-700">Buka WA & kirim</div>
                  </button>
                  <button onClick={shareTelegram} className="group flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-sky-200 bg-sky-50 hover:bg-sky-100 hover:border-sky-400 transition" data-testid="undangan-share-tg-btn">
                    <div className="h-12 w-12 rounded-full bg-sky-500 text-white grid place-items-center group-hover:scale-110 transition"><Send className="h-6 w-6" /></div>
                    <div className="font-bold text-sm text-sky-800">Telegram</div>
                    <div className="text-[10px] text-sky-700">Bagikan ke grup</div>
                  </button>
                  <button onClick={shareFB} className="group flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-blue-200 bg-blue-50 hover:bg-blue-100 hover:border-blue-400 transition" data-testid="undangan-share-fb-btn">
                    <div className="h-12 w-12 rounded-full bg-blue-600 text-white grid place-items-center group-hover:scale-110 transition"><Facebook className="h-6 w-6" /></div>
                    <div className="font-bold text-sm text-blue-800">Facebook</div>
                    <div className="text-[10px] text-blue-700">Post ke timeline</div>
                  </button>
                  <button onClick={shareTwitter} className="group flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-slate-300 bg-white hover:bg-slate-50 hover:border-slate-500 transition" data-testid="undangan-share-tw-btn">
                    <div className="h-12 w-12 rounded-full bg-black text-white grid place-items-center group-hover:scale-110 transition"><Twitter className="h-6 w-6" /></div>
                    <div className="font-bold text-sm text-slate-800">X / Twitter</div>
                    <div className="text-[10px] text-slate-600">Bagikan tweet</div>
                  </button>
                  <button onClick={() => doCopy(shareUrl, "Link")} className="group flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-amber-200 bg-amber-50 hover:bg-amber-100 hover:border-amber-400 transition" data-testid="undangan-copy-link-btn">
                    <div className="h-12 w-12 rounded-full bg-amber-500 text-white grid place-items-center group-hover:scale-110 transition"><Copy className="h-6 w-6" /></div>
                    <div className="font-bold text-sm text-amber-800">Salin Link</div>
                    <div className="text-[10px] text-amber-700 break-all px-1 line-clamp-1">{shareUrl}</div>
                  </button>
                  <button onClick={nativeShare} className="group flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-100 to-amber-100 hover:from-emerald-200 hover:to-amber-200 transition" data-testid="undangan-native-share-btn">
                    <div className="h-12 w-12 rounded-full brand-gradient text-white grid place-items-center group-hover:scale-110 transition"><Share2 className="h-6 w-6" /></div>
                    <div className="font-bold text-sm text-slate-800">Bagikan…</div>
                    <div className="text-[10px] text-slate-600">Pilih aplikasi lain</div>
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Right: QR Code */}
          <div className="lg:col-span-2 space-y-4">
            <Card className="border-2 border-amber-300 bg-gradient-to-br from-white to-amber-50 shadow-xl sticky top-24">
              <CardContent className="p-6 text-center">
                <div className="text-xs uppercase tracking-widest font-bold text-amber-700 mb-1">Scan QR untuk Bergabung</div>
                <h3 className="font-bold text-slate-800 text-lg mb-4" style={{ fontFamily: "Poppins, sans-serif" }}>QR Code Undangan</h3>
                <div className="relative inline-block p-4 bg-white rounded-2xl border-2 border-amber-300 shadow-md">
                  <img src={qrUrl} alt="QR Code Undangan Warga DM Residence" className="h-64 w-64 md:h-72 md:w-72 object-contain" data-testid="undangan-qr-img" />
                  {liveLogo && (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      <img src={liveLogo} alt="" className="h-14 w-14 rounded-full border-4 border-white bg-white shadow-lg object-cover" />
                    </div>
                  )}
                </div>
                <p className="mt-4 text-xs text-slate-500 leading-relaxed">
                  Cetak & tempel QR ini di papan pengumuman blok, atau share ke grup WA. Warga tinggal scan langsung buka aplikasi.
                </p>
                <div className="mt-4 flex gap-2 justify-center flex-wrap">
                  <Button onClick={downloadQR} variant="outline" size="sm" className="border-amber-300 text-amber-800 hover:bg-amber-100" data-testid="undangan-qr-download-btn"><Download className="h-4 w-4 mr-1" />Unduh QR</Button>
                  <Button onClick={() => doCopy(qrUrl, "URL QR")} variant="outline" size="sm" className="border-slate-300" data-testid="undangan-qr-copy-btn"><Copy className="h-4 w-4 mr-1" />Salin URL QR</Button>
                </div>
                <div className="mt-4 pt-4 border-t border-amber-200 text-left">
                  <div className="text-[10px] uppercase tracking-widest font-bold text-slate-500 mb-1">Link Aplikasi</div>
                  <div className="text-xs font-mono text-slate-700 break-all bg-slate-100 rounded-lg px-3 py-2 border">{shareUrl}</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Tips */}
        <Card className="mt-6 border-slate-100 bg-white">
          <CardContent className="p-6">
            <h3 className="font-bold text-slate-800 mb-3" style={{ fontFamily: "Poppins, sans-serif" }}>💡 Tips Membagikan Undangan</h3>
            <div className="grid md:grid-cols-3 gap-4 text-sm text-slate-600">
              <div className="flex gap-2"><span className="text-emerald-600 font-bold">1.</span> Kirim ke <b>Grup WhatsApp blok</b> masing-masing agar semua tetangga menerima info yang sama.</div>
              <div className="flex gap-2"><span className="text-emerald-600 font-bold">2.</span> Cetak <b>QR Code</b> dalam ukuran A5 dan tempel di <b>papan pengumuman</b> atau pos satpam.</div>
              <div className="flex gap-2"><span className="text-emerald-600 font-bold">3.</span> Edit pesan agar terasa <b>personal</b> — sebutkan nama blok atau ajakan khusus dari Ketua RT.</div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default BagikanUndangan;
